import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Container, H1, Input, Loader, Table, Alert } from '@itds/elements/index.js';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchVendors,
  setCurrentPage,
  setSearchQuery,
  clearCache
} from '@redux/carrierSlices/vendorSlice';
import Notification from '@carrier/components/Notification';
import DOMPurify from 'dompurify';
import NewDrawer from './New';
import { CarrierContext } from '../../contexts/CarrierContext';
import { debounce } from 'lodash';

const List = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { vendors, pagination, loading, error, currentPage, searchQuery } = useSelector(
    state => state.vendors
  );
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [showNewDrawer, toggleNewDrawer] = useState(false);
  const { notification, setNotification } = useContext(CarrierContext);

  useEffect(() => {
    dispatch(fetchVendors({ page: currentPage, search: searchQuery }));
  }, [dispatch, currentPage, searchQuery]);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce(value => {
        dispatch(setSearchQuery(value));
      }, 500),
    [dispatch]
  );

  const handleSearchChange = e => {
    let value = e.target.value.trimStart();

    value = DOMPurify.sanitize(value);
    setLocalSearch(value);
    debouncedSetSearchTerm(value);
  };

  const handleSearchKeyDown = e => {
    if (e.keyCode === 13) {
      debouncedSetSearchTerm.cancel();
      dispatch(setSearchQuery(localSearch));
      dispatch(setCurrentPage(1));
      dispatch(clearCache());
    }
  };
  const handlePageChange = page => {
    window.scrollTo(0, 0);
    dispatch(setCurrentPage(page));
    dispatch(fetchVendors({ page, search: searchQuery }));
  };

  const handleNewVendor = () => {
    toggleNewDrawer(false);
    dispatch(fetchVendors({ page: currentPage, search: searchQuery }));
  };

  return (
    <Container>
      {error && <Alert type='error'>{error}</Alert>}

      <NewDrawer
        onClose={() => toggleNewDrawer(false)}
        onSave={handleNewVendor}
        show={showNewDrawer}
      />

      <H1>Vendors</H1>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
        <Input
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          placeholder='Search by name...'
          style={{ width: 300 }}
          value={localSearch}
        />
        <div style={{ marginLeft: 'auto' }}>
          <Button onClick={() => toggleNewDrawer(true)}>Create Vendor</Button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <Table
          apiPagination={{ ...pagination, current_page: currentPage, onClick: handlePageChange }}
          columns={[
            {
              id: 'name',
              label: 'Name',
              is_sortable: true,
              styles: { padding: '30px 15px', verticalAlign: 'top' }
            },
            {
              id: 'id',
              label: 'ID',
              styles: { padding: '30px 15px', verticalAlign: 'top' }
            },
            {
              id: 'vendor_carrier_relationships',
              is_sortable: true,
              label: '# of Relationships',
              render: vendor =>
                vendor && vendor.vendor_carrier_relationships
                  ? vendor.vendor_carrier_relationships.length
                  : null,
              styles: { textAlign: 'center' }
            }
          ]}
          data={vendors}
          defaultSortColumn={{ id: 'name' }}
          onRowClick={vendor => navigate(`/vendors/${vendor.id}/general`)}
        />
      )}
      <Notification {...notification} />
    </Container>
  );
};

export default List;
